import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import {
  type ReturnTypeFromUseDocumentationPage,
  type ReturnTypeFromUseDocumentationPageMetadata,
  useDocumentationPage,
  useDocumentationPageMetadata,
} from '../api/pages';
import {
  type ReturnTypeFromUseDocumentationSpaces,
  useDocumentationSpaces,
} from '../api/spaces';

// The reader's page view-model: the published page plus its metadata. Both
// come from user-facing endpoints — the admin `documentation_admin_pageDetail`
// query 403s for the non-admin staff who use the reader.
interface ResolvedPageDetail {
  page: NonNullable<ReturnTypeFromUseDocumentationPage>;
  metadata: ReturnTypeFromUseDocumentationPageMetadata[];
}

// Where the reader currently is. The space and page live in one value so
// they can't drift apart, and "a page with no space" is unrepresentable.
// Navigating builds this path from the user's steps (home → space →
// article) rather than tracking an id and a slug separately.
export type ReaderLocation =
  | { view: 'home' }
  | { view: 'space'; spaceId: number }
  | { view: 'page'; spaceId: number; pageSlug: string };

// The active reader view. Search is a home-screen-local concern (see
// ReaderSearch), not a navigation destination, so it isn't represented here.
export type ReaderView = ReaderLocation['view'];

const HOME: ReaderLocation = { view: 'home' };

export function locationSpaceId(location: ReaderLocation): number | null {
  return location.view === 'home' ? null : location.spaceId;
}

export function sameLocation(a: ReaderLocation, b: ReaderLocation): boolean {
  if (a.view === 'home' || b.view === 'home') return a.view === b.view;
  if (a.spaceId !== b.spaceId) return false;
  const aSlug = a.view === 'page' ? a.pageSlug : null;
  const bSlug = b.view === 'page' ? b.pageSlug : null;
  return aSlug === bSlug;
}

// All reader navigation/shell state lives in one tree so every transition is
// a single atomic update. Before this, a "select a space" meant three
// coordinated setState calls (selection + cleared search + cleared history)
// that could only stay correct by convention.
export interface ReaderState {
  isOpen: boolean;
  isFullScreen: boolean;
  location: ReaderLocation;
  // Back-stack of visited locations. Every navigation (and the breadcrumb
  // "up to space") pushes the location being left, so the header back
  // button walks the full path — home → space → article → … — not just
  // article → article.
  history: ReaderLocation[];
}

export const INITIAL_STATE: ReaderState = {
  isOpen: false,
  isFullScreen: false,
  location: HOME,
  history: [],
};

export type ReaderAction =
  | { type: 'OPEN' }
  | { type: 'CLOSE' }
  | { type: 'TOGGLE_FULL_SCREEN' }
  // Navigate to a destination (home/space/article). Pushes the location
  // being left onto the back-stack. No-op when already at that destination.
  | { type: 'NAVIGATE'; to: ReaderLocation }
  | { type: 'GO_BACK' };

export function readerReducer(
  state: ReaderState,
  action: ReaderAction,
): ReaderState {
  switch (action.type) {
    case 'OPEN':
      return { ...state, isOpen: true };
    // Closing fully resets the reader so it reopens at home with no leftover
    // history.
    case 'CLOSE':
      return INITIAL_STATE;
    case 'TOGGLE_FULL_SCREEN':
      return { ...state, isFullScreen: !state.isFullScreen };
    case 'NAVIGATE': {
      const { to } = action;
      // Re-selecting the current location is a no-op — don't stack a dupe.
      if (sameLocation(state.location, to)) return state;
      return {
        ...state,
        location: to,
        history: [...state.history, state.location],
      };
    }
    case 'GO_BACK': {
      if (state.history.length === 0) return state;
      const previous = state.history[state.history.length - 1];
      return {
        ...state,
        location: previous,
        history: state.history.slice(0, -1),
      };
    }
    default:
      return state;
  }
}

// The context is split into stable actions and changing state so action-only
// consumers (the article subtree) never re-render from navigation changes.

// Stable for the provider's lifetime (the slug helpers only change when the
// spaces list first loads). Action-only consumers never re-render from state.
interface ReaderActions {
  open: () => void;
  close: () => void;
  toggleFullScreen: () => void;
  navigate: (to: ReaderLocation) => void;
  // Slug-based navigation. The provider owns the spaces query, so it resolves
  // a space slug to its id here instead of every caller re-deriving it.
  navigateToSpace: (spaceSlug: string) => void;
  navigateToPage: (spaceSlug: string, pageSlug: string) => void;
  goBack: () => void;
}

// Navigation / shell state. Changes on navigation, open/close, full-screen,
// and spaces loading.
interface ReaderStateValue {
  isOpen: boolean;
  // The single navigation path. `currentSpace` is that location's space
  // resolved against the spaces query (the provider owns both), so consumers
  // get the space object without juggling id vs slug.
  location: ReaderLocation;
  // The location's view (home/space/article). Kept as its own field so
  // consumers don't reach into `location.view` directly.
  view: ReaderView;
  currentSpace: ReturnTypeFromUseDocumentationSpaces | null;
  // The page detail (`{ page, metadata }`) at the current location, resolved
  // the same way as `currentSpace` (live from the page-detail query owned
  // here). This is the single page-detail fetch — `useReaderPage` and the
  // breadcrumb read it rather than running their own.
  currentPageDetail: ResolvedPageDetail | null;
  isLoadingPage: boolean;
  canGoBack: boolean;
  // Full-screen is reader-shell state, kept here so the header and the
  // bounds-sync don't need it threaded through props. Reset on close.
  isFullScreen: boolean;
  // Reader data — fetched once here so it doesn't have to be prop-drilled
  // into the home view.
  spaces: ReturnTypeFromUseDocumentationSpaces[] | undefined;
  // Distinguishes "spaces still loading" from "no spaces", so the shell can
  // show a loader instead of flashing the empty state.
  isLoadingSpaces: boolean;
}

const ReaderActionsContext = createContext<ReaderActions | null>(null);
const ReaderStateContext = createContext<ReaderStateValue | null>(null);

interface DocumentationReaderProviderProps {
  children: React.ReactNode;
}

export function DocumentationReaderProvider({
  children,
}: DocumentationReaderProviderProps) {
  const [state, dispatch] = useReducer(readerReducer, INITIAL_STATE);

  const { data: spaces, isLoading: isLoadingSpaces } = useDocumentationSpaces();

  const spaceId = locationSpaceId(state.location);
  const currentSpace = useMemo(
    () => spaces?.find((space) => space.id === spaceId) ?? null,
    [spaces, spaceId],
  );

  // Resolve the current page centrally, mirroring `currentSpace`, so the
  // breadcrumb and content view share one fetch. Page and metadata both come
  // from user-facing endpoints — readers are non-admin staff. Metadata is a
  // follow-up fetch keyed on the page id.
  const { data: page, isLoading: isLoadingPageQuery } = useDocumentationPage({
    spaceSlug: currentSpace?.slug ?? '',
    pageSlug: state.location.view === 'page' ? state.location.pageSlug : '',
  });
  const { data: metadata, isLoading: isLoadingMetadata } =
    useDocumentationPageMetadata(page?.id);
  const currentPageDetail = useMemo<ResolvedPageDetail | null>(
    () => (page ? { page, metadata: metadata ?? [] } : null),
    [page, metadata],
  );
  // Treat the page as still loading until its metadata lands, so block-access
  // gating never runs against empty metadata for a frame.
  const isLoadingPage =
    isLoadingPageQuery || (page?.id != null && isLoadingMetadata);

  const open = useCallback(() => dispatch({ type: 'OPEN' }), []);
  const close = useCallback(() => dispatch({ type: 'CLOSE' }), []);
  const toggleFullScreen = useCallback(
    () => dispatch({ type: 'TOGGLE_FULL_SCREEN' }),
    [],
  );
  const navigate = useCallback(
    (to: ReaderLocation) => dispatch({ type: 'NAVIGATE', to }),
    [],
  );
  const navigateToSpace = useCallback(
    (spaceSlug: string) => {
      const space = spaces?.find((candidate) => candidate.slug === spaceSlug);
      if (space) navigate({ view: 'space', spaceId: space.id });
    },
    [spaces, navigate],
  );
  const navigateToPage = useCallback(
    (spaceSlug: string, pageSlug: string) => {
      const space = spaces?.find((candidate) => candidate.slug === spaceSlug);
      if (space) navigate({ view: 'page', spaceId: space.id, pageSlug });
    },
    [spaces, navigate],
  );
  const goBack = useCallback(() => dispatch({ type: 'GO_BACK' }), []);

  const actions = useMemo<ReaderActions>(
    () => ({
      open,
      close,
      toggleFullScreen,
      navigate,
      navigateToSpace,
      navigateToPage,
      goBack,
    }),
    [
      open,
      close,
      toggleFullScreen,
      navigate,
      navigateToSpace,
      navigateToPage,
      goBack,
    ],
  );

  // Depends on the individual slices, not the whole `state` object, so an
  // unrelated slice changing never widens the re-render set.
  const readerState = useMemo<ReaderStateValue>(
    () => ({
      isOpen: state.isOpen,
      location: state.location,
      view: state.location.view,
      currentSpace,
      currentPageDetail,
      isLoadingPage,
      canGoBack: state.history.length > 0,
      isFullScreen: state.isFullScreen,
      spaces,
      isLoadingSpaces,
    }),
    [
      state.isOpen,
      state.location,
      state.history.length,
      state.isFullScreen,
      currentSpace,
      currentPageDetail,
      isLoadingPage,
      spaces,
      isLoadingSpaces,
    ],
  );

  return (
    <ReaderActionsContext.Provider value={actions}>
      <ReaderStateContext.Provider value={readerState}>
        {children}
      </ReaderStateContext.Provider>
    </ReaderActionsContext.Provider>
  );
}

function useReaderContext<T>(
  context: React.Context<T | null>,
  hookName: string,
): T {
  const value = useContext(context);
  if (!value) {
    throw new Error(
      `${hookName} must be used within a DocumentationReaderProvider`,
    );
  }
  return value;
}

export function useReaderActions(): ReaderActions {
  return useReaderContext(ReaderActionsContext, 'useReaderActions');
}

export function useReaderState(): ReaderStateValue {
  return useReaderContext(ReaderStateContext, 'useReaderState');
}
